import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import axiosInstance from "../../services/api/axios";
import {
  Search,
  Users,
  Heart,
  HeartOff,
  Flame,
  ArrowRight,
  ChevronDown,
  UserCircle,
  Phone,
  Home,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import usePagination from "../../hooks/usePagination";
import PaginationControls from "../../components/PaginationControls";

/** Pill-tab quick filters above the buyer list. Map each id to a `hasShortlisted`
 *  query value — the backend (`GET /customer/sales`) now supports the param. */
const FILTER_TABS = [
  { id: "shortlisted", label: "Shortlisted", icon: Heart, hasShortlisted: true },
  { id: "all", label: "All buyers", icon: Users, hasShortlisted: undefined },
  { id: "without", label: "Without shortlist", icon: HeartOff, hasShortlisted: false },
];

function getInitials(firstName, lastName) {
  const first = (firstName || "").trim();
  const last = (lastName || "").trim();
  if (!first && !last) return "?";
  return `${first.charAt(0) || ""}${last.charAt(0) || ""}`.toUpperCase() || "?";
}

function Buyers() {
  const navigate = useNavigate();
  const { userInfo } = useUser();
  const loginId = userInfo?.loginId;

  // "Shortlisted" is the default pill tab on mount — fetch goes out with
  // hasShortlisted=true on first load.
  const [activeFilter, setActiveFilter] = useState("shortlisted");
  const [selectedProject, setSelectedProject] = useState("");
  const [allowedProjects, setAllowedProjects] = useState([]);
  const [leadsMap, setLeadsMap] = useState({});

  // Live counts per pill tab. Each tab fetches `total` from the same endpoint
  // with its `hasShortlisted` variant so the chip stays accurate regardless of
  // the page we're on.
  const [tabCounts, setTabCounts] = useState({
    shortlisted: 0,
    all: 0,
    without: 0,
  });

  // Get customerId from userInfo
  const customerId =
    userInfo?.loginIdentifierDetails?.customer?.customerId ||
    userInfo?.loginIdentifierDetails?.customerId;

  // Fetch allowed projects on component mount. Backend scopes by RBAC
  // (accessible_project_ids derived from rbac_role_assignment), so a BV user
  // only sees projects they're assigned to.
  useEffect(() => {
    const fetchAllowedProjects = async () => {
      try {
        const params = {};
        if (customerId) params.customerId = customerId;
        const projectsResponse = await axiosInstance.get(`/projects`, { params });

        const projectsArray = Array.isArray(projectsResponse)
          ? projectsResponse
          : projectsResponse?.items || [];
        setAllowedProjects(projectsArray.filter(Boolean));
      } catch (error) {
        console.error("Error fetching allowed projects:", error);
        toast.error("Failed to fetch allowed projects");
      }
    };

    if (loginId) {
      fetchAllowedProjects();
    }
  }, [loginId, customerId]);

  // Resolve current tab's hasShortlisted value (undefined => omit param).
  const currentHasShortlisted = useMemo(() => {
    const tab = FILTER_TABS.find((t) => t.id === activeFilter);
    return tab ? tab.hasShortlisted : undefined;
  }, [activeFilter]);

  // Create fetch function for sales with pagination. The function re-creates
  // whenever the active filter changes, which triggers usePagination to refetch
  // (and resets to page 1 via the filter-change effect below).
  const fetchSalesBackend = useCallback(
    async ({ page, page_size, search }) => {
      try {
        // First, fetch allowed projects — backend RBAC-scopes to the caller.
        let allowedProjectIds = [];
        try {
          const params = {};
          if (customerId) params.customerId = customerId;
          const projectsResponse = await axiosInstance.get(`/projects`, { params });

          const projectsArray = Array.isArray(projectsResponse)
            ? projectsResponse
            : projectsResponse?.items || [];
          allowedProjectIds = projectsArray
            .map((p) => p?.projectId || p?.id)
            .filter(Boolean);
        } catch (error) {
          console.error("Error fetching allowed projects:", error);
          toast.error("Failed to fetch allowed projects");
          return {
            items: [],
            total: 0,
            page: 1,
            page_size: 30,
            total_pages: 1,
            has_next: false,
            has_previous: false,
          };
        }

        // If no allowed projects, return empty
        if (allowedProjectIds.length === 0) {
          return {
            items: [],
            total: 0,
            page: 1,
            page_size: 30,
            total_pages: 1,
            has_next: false,
            has_previous: false,
          };
        }

        // Build params: project scope + pagination + optional search +
        // optional hasShortlisted (new backend filter).
        const params = {
          page,
          page_size,
          ...(allowedProjectIds.length > 0 && { projectId: allowedProjectIds }),
          ...(search && { search }),
          ...(currentHasShortlisted !== undefined && {
            hasShortlisted: currentHasShortlisted,
          }),
        };

        const salesResponse = await axiosInstance.get("/customer/sales", {
          params,
        });

        // Handle both paginated and non-paginated responses
        let salesArray;
        if (salesResponse?.items && salesResponse.total !== undefined) {
          salesArray = salesResponse.items;
        } else if (Array.isArray(salesResponse)) {
          salesArray = salesResponse;
        } else {
          salesArray = [];
        }

        // Backend should already filter by projectId; keep client-side guard
        // as backup so we never render a sale outside the allowed set.
        salesArray = salesArray.filter((sale) => {
          const saleProjectId =
            sale.propertyData?.projectPhase?.project?.projectId;
          return saleProjectId && allowedProjectIds.includes(saleProjectId);
        });

        if (salesResponse?.items && salesResponse.total !== undefined) {
          // Backend pagination — respect its totals where possible. We still
          // re-slice to keep the client-side project filter consistent.
          const startIndex = (page - 1) * page_size;
          const endIndex = startIndex + page_size;
          const paginatedItems = salesArray.slice(startIndex, endIndex);
          return {
            items: paginatedItems,
            total: salesArray.length,
            page,
            page_size,
            total_pages: Math.ceil(salesArray.length / page_size),
            has_next: endIndex < salesArray.length,
            has_previous: page > 1,
          };
        } else {
          // Client-side pagination fallback for non-paginated responses.
          const startIndex = (page - 1) * page_size;
          const endIndex = startIndex + page_size;
          const paginatedItems = salesArray.slice(startIndex, endIndex);
          return {
            items: paginatedItems,
            total: salesArray.length,
            page,
            page_size,
            total_pages: Math.ceil(salesArray.length / page_size),
            has_next: endIndex < salesArray.length,
            has_previous: page > 1,
          };
        }
      } catch (error) {
        console.error("Error fetching sales data:", error);
        toast.error("Failed to load buyers data");
        return {
          items: [],
          total: 0,
          page: 1,
          page_size: 30,
          total_pages: 1,
          has_next: false,
          has_previous: false,
        };
      }
    },
    [customerId, currentHasShortlisted]
  );

  // Use the pagination hook with manual search
  const {
    data: salesData,
    pagination,
    searchInput,
    loading,
    error,
    goToPage,
    changePageSize,
    handleSearchInputChange,
    triggerSearch,
    // eslint-disable-next-line no-unused-vars
    refetch,
  } = usePagination(fetchSalesBackend, 30, "", true, true);

  // Reset to page 1 whenever the pill-tab filter changes. Implemented via
  // goToPage so it shares the hook's existing reset path.
  useEffect(() => {
    goToPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  // After sales load, fetch leads for each sale and build leadsMap. This is
  // the existing enrichment effect — preserved unchanged.
  useEffect(() => {
    if (!salesData || salesData.length === 0) return;

    const fetchLeadsForSales = async () => {
      const map = {};
      await Promise.all(
        salesData.map(async (sale) => {
          const propertyId = sale.propertyData?.propertyId;
          const userId = sale.user?.userId;
          const saleId = sale.customerSalesId;
          if (!propertyId || !userId || !saleId) return;
          try {
            const response = await axiosInstance.get("/interior-designers/leads", {
              params: { propertyId, userId, page: 1, page_size: 100 },
            });
            let leadsArray = [];
            if (Array.isArray(response)) leadsArray = response;
            else if (Array.isArray(response?.items)) leadsArray = response.items;
            else if (Array.isArray(response?.data?.items)) leadsArray = response.data.items;
            else if (Array.isArray(response?.data)) leadsArray = response.data;

            const totalNew = leadsArray.reduce((sum, l) => sum + (l.newRequests ?? 0), 0);
            const anyNew = leadsArray.some((l) => l.hasNewRequest || (l.newRequests ?? 0) > 0);
            map[saleId] = { newRequests: totalNew, hasNewRequest: anyNew };
          } catch {
            map[saleId] = { newRequests: 0, hasNewRequest: false };
          }
        })
      );
      setLeadsMap(map);
    };

    fetchLeadsForSales();
  }, [salesData]);

  // Fetch tab counts independently so the pill chips show live totals across
  // all three states (shortlisted / all / without) without paging through.
  // We fire a tiny page_size=1 request per state and read `total` off the
  // paginated envelope.
  useEffect(() => {
    if (!customerId && !loginId) return;
    let cancelled = false;

    const fetchCount = async (hasShortlisted) => {
      try {
        const params = {};
        if (customerId) params.customerId = customerId;
        const projectsResponse = await axiosInstance.get(`/projects`, { params });
        const projectsArray = Array.isArray(projectsResponse)
          ? projectsResponse
          : projectsResponse?.items || [];
        const allowedProjectIds = projectsArray
          .map((p) => p?.projectId || p?.id)
          .filter(Boolean);
        if (allowedProjectIds.length === 0) return 0;

        const salesParams = {
          page: 1,
          page_size: 1,
          projectId: allowedProjectIds,
          ...(hasShortlisted !== undefined && { hasShortlisted }),
        };
        const res = await axiosInstance.get("/customer/sales", { params: salesParams });
        if (res?.total !== undefined) return res.total;
        if (Array.isArray(res)) return res.length;
        if (Array.isArray(res?.items)) return res.items.length;
        return 0;
      } catch (e) {
        console.error("Error fetching buyer tab count:", e);
        return 0;
      }
    };

    (async () => {
      const [shortlistedCount, allCount, withoutCount] = await Promise.all([
        fetchCount(true),
        fetchCount(undefined),
        fetchCount(false),
      ]);
      if (!cancelled) {
        setTabCounts({
          shortlisted: shortlistedCount,
          all: allCount,
          without: withoutCount,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customerId, loginId]);

  // Client-side project filter (project select). Search is server-side via
  // the pagination hook's triggerSearch.
  const filteredSales = useMemo(() => {
    return (salesData || []).filter((sale) => {
      if (selectedProject) {
        const saleProjectId =
          sale.propertyData?.projectPhase?.project?.projectId;
        return saleProjectId && Number(selectedProject) === saleProjectId;
      }
      return true;
    });
  }, [salesData, selectedProject]);

  // Aggregate KPIs for the hero strip. Total/shortlisted/without come from
  // the live tab counts; "New requests" is summed from the leadsMap built
  // for the visible page.
  const kpis = useMemo(() => {
    const newRequests = Object.values(leadsMap).reduce(
      (sum, l) => sum + (l?.newRequests ?? 0),
      0
    );
    const activeBuyersWithRequests = Object.values(leadsMap).filter(
      (l) => l?.hasNewRequest
    ).length;
    return {
      total: tabCounts.all,
      shortlisted: tabCounts.shortlisted,
      without: tabCounts.without,
      newRequests,
      activeBuyersWithRequests,
    };
  }, [tabCounts, leadsMap]);

  const handleViewDetails = (data) => {
    navigate(`/bv-user/buyer/${data.customerSalesId}/details`);
  };

  const handleSearchChange = (value) => {
    handleSearchInputChange(value);
    // If search box is cleared, automatically trigger search to reset.
    if (!value || value.trim() === "") {
      triggerSearch();
    }
  };

  const hasActiveFilters = Boolean(searchInput) || Boolean(selectedProject);
  const clearFilters = () => {
    setSelectedProject("");
    if (searchInput) {
      handleSearchInputChange("");
      triggerSearch();
    }
  };

  // Pill tabs with live counts.
  const tabDefs = [
    { id: "shortlisted", label: "Shortlisted", icon: Heart, count: tabCounts.shortlisted },
    { id: "all", label: "All buyers", icon: Users, count: tabCounts.all },
    { id: "without", label: "Without shortlist", icon: HeartOff, count: tabCounts.without },
  ];

  const renderEmptyState = () => {
    const isFiltered =
      hasActiveFilters || activeFilter !== "all";
    return (
      <div className="px-6 py-16 text-center">
        <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
          <Users className="text-slate-400" size={24} />
        </div>
        <p className="mt-4 text-base font-semibold text-slate-900">
          {isFiltered ? "No buyers match this filter" : "No buyers yet"}
        </p>
        <p className="mt-1 text-sm text-slate-500">
          {isFiltered
            ? "Try a different pill tab, clear the search, or choose another project."
            : "Buyers will appear here once sales are recorded on your assigned projects."}
        </p>
      </div>
    );
  };

  return (
    <div className="px-6 py-6 space-y-6">
      {/* Slate gradient hero band with KPI strip */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)",
          }}
        />
        <div className="relative px-6 py-6 sm:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
                <Sparkles size={12} />
                Buildvizer User
              </div>
              <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">
                Buyers
              </h1>
              <p className="mt-1 text-sm text-white/70">
                Buyers on your assigned projects. Filter by shortlist activity
                and open a buyer to review their sale and design requests.
              </p>
            </div>
          </div>

          {/* KPI strip */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <Users size={13} />
                Total buyers
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">
                {kpis.total}
              </div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-flamingo-200 text-xs">
                <Heart size={13} />
                Shortlisted
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">
                {kpis.shortlisted}
              </div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-amber-200 text-xs">
                <Flame size={13} />
                New requests
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">
                {kpis.newRequests}
              </div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-emerald-200 text-xs">
                <Sparkles size={13} />
                Buyers with activity
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">
                {kpis.activeBuyersWithRequests}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter card — pill tabs + search + project select */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {/* Pill tab nav */}
          <div className="overflow-x-auto">
            <div className="inline-flex items-center gap-1 rounded-2xl bg-slate-50 p-1.5">
              {tabDefs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveFilter(tab.id)}
                    className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-all whitespace-nowrap ${
                      isActive
                        ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                        : "text-slate-600 hover:bg-white hover:text-slate-800"
                    }`}
                  >
                    <Icon size={13} />
                    {tab.label}
                    <span
                      className={`tabular-nums rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                        isActive
                          ? "bg-white/25 text-white"
                          : "bg-slate-200 text-slate-700"
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search + project select */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Search buyers…"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && triggerSearch()}
                disabled={loading}
                className="w-full sm:w-64 rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-8 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all focus:bg-white focus:border-flamingo-400 focus:ring-2 focus:ring-flamingo-200 disabled:cursor-not-allowed disabled:opacity-60"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    handleSearchInputChange("");
                    triggerSearch();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="relative">
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                disabled={loading || allowedProjects.length === 0}
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 disabled:cursor-not-allowed disabled:opacity-60 min-w-[180px]"
              >
                <option value="">All projects</option>
                {allowedProjects.map((project) => (
                  <option key={project.projectId} value={project.projectId}>
                    {project.projectName}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                size={16}
              />
            </div>
          </div>
        </div>

        {/* Result count + clear */}
        {!loading && (
          <div className="mt-3 border-t border-slate-100 pt-2.5 text-xs text-slate-500 flex items-center justify-between">
            <span>
              Showing{" "}
              <span className="font-semibold text-slate-700">
                {filteredSales.length}
              </span>{" "}
              {filteredSales.length === 1 ? "buyer" : "buyers"}
              {pagination?.total !== undefined && (
                <span className="text-slate-400">
                  {" "}
                  · {pagination.total} on this filter
                </span>
              )}
            </span>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="font-medium text-flamingo-600 hover:text-flamingo-700"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Error / list */}
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-white px-6 py-10 text-center shadow-sm">
          <p className="text-rose-700 font-medium">Error loading buyers</p>
          <p className="mt-1 text-sm text-slate-500">
            {typeof error === "string" ? error : error?.message || "Please try again."}
          </p>
        </div>
      ) : loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-flamingo-200 border-t-flamingo-500" />
          <p className="mt-3 text-sm text-slate-500">Loading buyers…</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {filteredSales.length === 0 ? (
            renderEmptyState()
          ) : (
            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredSales.map((sale) => {
                const user = sale.user || {};
                const property = sale.propertyData || {};
                const projectPhase = property.projectPhase || {};
                const project = projectPhase.project || {};
                const name =
                  `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                  "Unnamed buyer";
                const mobile =
                  user.mobileNoCalling || user.mobileNoWa || "—";
                const propertyNo = property.propertyNo || "—";
                const projectName =
                  sale.projectName || project.projectName || "—";
                const initials = getInitials(user.firstName, user.lastName);

                const leadInfo = leadsMap[sale.customerSalesId];
                const newRequests = leadInfo?.newRequests ?? 0;
                const hasNewRequest = Boolean(leadInfo?.hasNewRequest);

                return (
                  <div
                    key={
                      sale.customerSalesId ??
                      property.propertyId ??
                      `${user.userId}-${propertyNo}`
                    }
                    onClick={() => handleViewDetails(sale)}
                    className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                  >
                    {/* Card header — avatar + name + new request chip */}
                    <div className="flex items-start justify-between gap-2 p-4 pb-2">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-flamingo-500 to-flamingo-600 text-sm font-semibold text-white shadow-sm">
                          {initials === "?" ? (
                            <UserCircle size={20} />
                          ) : (
                            initials
                          )}
                        </div>
                        <div className="min-w-0">
                          <h3
                            className="truncate text-sm font-semibold text-slate-900"
                            title={name}
                          >
                            {name}
                          </h3>
                          <p className="truncate text-[11px] text-slate-500" title={mobile}>
                            <Phone size={10} className="inline -mt-0.5 mr-0.5 text-slate-400" />
                            <span className="tabular-nums">{mobile}</span>
                          </p>
                        </div>
                      </div>
                      {(hasNewRequest || newRequests > 0) && (
                        <span
                          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-flamingo-50 px-2 py-0.5 text-[10px] font-semibold text-flamingo-700 border border-flamingo-200"
                          title="New design requests"
                        >
                          <Flame size={10} />
                          {newRequests > 0 ? `+${newRequests}` : "New"}
                        </span>
                      )}
                    </div>

                    {/* Card body — property & project info */}
                    <div className="px-4 pb-3 space-y-1.5">
                      <div className="flex items-center gap-1.5 text-xs text-slate-600">
                        <Home size={12} className="shrink-0 text-slate-400" />
                        <span className="font-medium text-slate-700 tabular-nums">
                          Unit {propertyNo}
                        </span>
                        {property.buildingNo && (
                          <span className="text-slate-400">
                            · Bldg {property.buildingNo}
                          </span>
                        )}
                      </div>
                      <p
                        className="truncate text-xs text-slate-500"
                        title={projectName}
                      >
                        {projectName}
                      </p>
                      {projectPhase?.name && (
                        <p
                          className="truncate text-[11px] text-slate-400"
                          title={projectPhase.name}
                        >
                          {projectPhase.name}
                        </p>
                      )}
                    </div>

                    {/* Footer CTA */}
                    <div className="mt-auto border-t border-slate-100 px-4 py-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-slate-500">
                          View details
                        </span>
                        <ArrowRight
                          size={14}
                          className="text-slate-400 transition-all group-hover:translate-x-0.5 group-hover:text-flamingo-500"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Inline pagination — sits inside the same card as the list. */}
          {filteredSales.length > 0 && pagination.total > 0 && (
            <div className="border-t border-slate-100 px-4 py-3">
              <PaginationControls
                pagination={pagination}
                onPageChange={goToPage}
                onPageSizeChange={changePageSize}
                itemName="buyers"
                variant="list"
                showInfo={true}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Buyers;
